import { DsHeading } from '@frontends/ds';
import { useLocale } from '@frontends/util-i18n';
import { useTheme } from '@frontends/util-theme-provider';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useEntityConfig } from '@/hooks/useEntityConfig.tsx';
import { IB_DESK_PATH } from '@/hooks/useIbDeskSection';
import useIsIbDeskAvailable from '@/hooks/useIsIbDeskAvailable';
import { replaceLanguageInUrl } from '@/lib/utils.ts';
import { Service } from '@/types/Services.ts';

const WebpFiles: Record<string, { default: string }> = import.meta.glob('@/assets/images/dashboard/**/*.webp', {
  eager: true,
});

const Services = memo(function Services() {
  const localeContext = useLocale();
  const { t } = useTranslation();
  const { resolvedMode: theme } = useTheme();
  const navigate = useNavigate();
  const config = useEntityConfig();

  const handleClick = useCallback(
    ({ serviceUrl, id }: { serviceUrl: string; id: string }) => {
      if (id === 'certificates' || id === 'trading-ideas' || id === 'ib-desk') {
        navigate(serviceUrl);

        return;
      }

      const newWindow = window.open(serviceUrl, '_blank', 'noopener,noreferrer');

      if (newWindow) newWindow.opener = null;
    },
    [navigate],
  );

  const isIbDeskAvailable = useIsIbDeskAvailable();

  const services: Service[] = useMemo(() => {
    const entityPrefix = `/src/assets/images/dashboard/${config.VITE_ENTITY}/${theme}`;

    return [
      {
        id: 'instant-top-up',
        title: t('instant_top_up_title'),
        description: t('instant_top_up_desc'),
        imageUrl: `${entityPrefix}/Instant-Top Up.webp`,
        serviceUrl: replaceLanguageInUrl(config.VITE_INSTANT_TOP_UP_URL, localeContext),
      },
      {
        id: 'trading-view',
        title: t('trading_view_title'),
        description: t('trading_view_desc'),
        imageUrl: `${entityPrefix}/TradingView.webp`,
        // No `?theme=` / `?locale=`: the terminal resolves both from the same
        // `client_preferences` row, and from the cookie on the shared root domain before
        // it knows the client. Passing them would mark them as a deliberate choice —
        // outranking the stored row and provoking a write back on every visit.
        serviceUrl: config.VITE_TRADING_VIEW_URL,
      },
      {
        id: 'social-trading',
        title: t('social_trading_title'),
        description: t('social_trading_desc'),
        imageUrl: `${entityPrefix}/Social Trading.webp`,
        serviceUrl: '/services/social-trading',
      },
      {
        id: 'certificates',
        title: t('certificates_title'),
        description: t('certificates_description'),
        imageUrl: `${entityPrefix}/Certificate.webp`,
        serviceUrl: '/services/certificates',
      },
      {
        id: 'promo',
        title: t('promo_title'),
        description: t('promo_description'),
        imageUrl: `${entityPrefix}/Promocodes.webp`,
        serviceUrl: replaceLanguageInUrl(config.VITE_URL_PROMO, localeContext),
      },
      // {
      //   id: 'help-desk',
      //   title: t("get_help_title"),
      //   description: t("get_help_desc"),
      //   imageUrl: `${entityPrefix}/Help Desk.webp`,
      //   serviceUrl: replaceLanguageInUrl(config.VITE_HELP_DESK_URL, localeContext),
      // },
      // {
      //   id: 'client-portal',
      //   title: t("client_portal_title"),
      //   description: t("client_portal_desc"),
      //   imageUrl: `${entityPrefix}/Client Portal.webp`,
      //   serviceUrl: replaceLanguageInUrl(config.VITE_CLIENT_PORTAL_URL, localeContext),
      // },
      // {
      //   id: 'super-charts',
      //   title: t("super_charts_title"),
      //   description: t("super_charts_desc"),
      //   imageUrl: `${entityPrefix}/SuperCharts (MT5).webp`,
      //   serviceUrl: config.VITE_SUPER_CHARTS_URL,
      // },
      {
        id: 'ib-panel',
        title: t('ib_panel_title'),
        description: t('ib_panel_desc'),
        imageUrl: `${entityPrefix}/Partners Panel.webp`,
        serviceUrl: replaceLanguageInUrl(config.VITE_IB_PANEL_URL, localeContext),
        disabled: isIbDeskAvailable,
      },
      {
        id: 'ib-desk',
        title: t('ib_desk'),
        description: t('ib_desk_desc'),
        imageUrl: `${entityPrefix}/Partners Panel.webp`,
        serviceUrl: `${IB_DESK_PATH}/overview`,
        disabled: !isIbDeskAvailable,
      },
      {
        id: 'trading-ideas',
        title: t('trading_ideas_title'),
        description: t('trading_ideas_description'),
        imageUrl: `${entityPrefix}/TradingIdeas.webp`,
        serviceUrl: '/services/trading-ideas',
      },
    ];
  }, [isIbDeskAvailable, t, theme, localeContext.locale.language]);

  return (
    <>
      <div
        className="
            grid
            grid-cols-1
            sm:grid-cols-2
            md:grid-cols-2
            lg:grid-cols-2
            xl:grid-cols-3
            2xl:grid-cols-3
            3xl:grid-cols-4
            4xl:grid-cols-5
            select-none
            gap-5
        "
      >
        {services
          .filter((service: Service) => !service?.disabled)
          .map(({ id, serviceUrl, imageUrl, title, description }, index) => (
            <article
              key={`${title}-${index}`}
              onClick={() => handleClick({ serviceUrl, id })}
              className="group select-none cursor-pointer"
            >
              <div
                className="
                min-h-[200px] max-md:max-h-[220px]
                relative w-full h-full aspect-[16/10]
                overflow-hidden rounded-[24px]
                transition-[translate,box-shadow] duration-300 ease-out
                shadow-[0_8px_25px_-15px_rgba(9,18,36,0.35)]
                motion-safe:group-hover:-translate-y-1.5
                group-hover:shadow-[0_16px_40px_-20px_rgba(9,18,36,0.45)]
                dark:group-hover:ring-1 dark:group-hover:ring-borderPrimary
                motion-safe:group-active:translate-y-0
                group-active:shadow-[0_8px_25px_-15px_rgba(9,18,36,0.35)]
              "
              >
                <img
                  alt={title}
                  src={WebpFiles[imageUrl]?.default || imageUrl}
                  className="absolute inset-0 h-full w-full object-cover pointer-events-none"
                />
                <div className="absolute bottom-0 w-full z-10 pointer-events-none">
                  <div className="px-3 py-2">
                    <div className="bg-backgroundInverse/40 dark:bg-backgroundCanvas/50 rounded-xl p-4 backdrop-blur-xl">
                      <DsHeading
                        level={2}
                        size="2xs"
                        className="mb-1.5 text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]"
                      >
                        {title}
                      </DsHeading>
                      <p className="text-base text-white/90 dark:text-white/80 drop-shadow-[0_1px_4px_rgba(0,0,0,0.3)]">
                        {description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          ))}
      </div>
    </>
  );
});

export default Services;
